import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyMembership } from '../contexts/CompanyMembershipContext';
import { signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

export default function RoleDiagnostic() {
  const { currentUser } = useAuth();
  const { companyId, currentUserRole, permissions, isLoading, refreshMembership } = useCompanyMembership();
  const [userDoc, setUserDoc] = useState(null);
  const [companyUserDoc, setCompanyUserDoc] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    async function loadUserInfo() {
      if (!currentUser) return;

      try {
        // Get top-level user doc
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        setUserDoc(userDocSnap.exists() ? userDocSnap.data() : null);

        // Get company user doc if companyId exists
        if (userDocSnap.exists() && userDocSnap.data().companyId) {
          const userCompanyId = userDocSnap.data().companyId;
          const companyUserRef = doc(db, 'companies', userCompanyId, 'users', currentUser.uid);
          const companyUserSnap = await getDoc(companyUserRef);
          setCompanyUserDoc(companyUserSnap.exists() ? companyUserSnap.data() : null);
          
          // If company user doc doesn't exist but companyId is set, create it
          if (!companyUserSnap.exists()) {
            console.warn('Company user document missing! Creating it...');
            try {
              await setDoc(companyUserRef, {
                email: currentUser.email || '',
                name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
                role: 'admin', // Default to admin if company owner
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                invitedBy: null,
                lastLoginAt: new Date().toISOString(),
              });
              console.log('✅ Created missing company user document');
              // Reload after creation
              setTimeout(() => window.location.reload(), 1000);
            } catch (error) {
              console.error('Error creating company user doc:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    }

    loadUserInfo();
  }, [currentUser, companyId]);

  const makeAdmin = async () => {
    if (!currentUser) {
      setMessage('Error: Not logged in');
      return;
    }
    
    // Get companyId from userDoc if not in context
    const targetCompanyId = companyId || userDoc?.companyId;
    
    if (!targetCompanyId) {
      setMessage('Error: No company ID found. Please contact support.');
      return;
    }

    setFixing(true);
    setMessage('');

    try {
      const companyUserRef = doc(db, 'companies', targetCompanyId, 'users', currentUser.uid);
      const companyUserSnap = await getDoc(companyUserRef);

      if (!companyUserSnap.exists()) {
        // Create user doc if it doesn't exist
        await setDoc(companyUserRef, {
          email: currentUser.email || '',
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          role: 'admin',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          invitedBy: null,
          lastLoginAt: new Date().toISOString(),
        });
        setMessage('✅ Created user document and set role to admin! Please refresh the page.');
      } else {
        // Update existing doc
        await updateDoc(companyUserRef, {
          role: 'admin',
          isActive: true,
          updatedAt: new Date().toISOString(),
        });
        setMessage('✅ Updated role to admin! Please refresh the page to see changes.');
      }

      // Reload after a moment
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error making admin:', error);
      setMessage(`❌ Error: ${error.message}. You may need to ask another admin to do this, or use Firebase Console.`);
    } finally {
      setFixing(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading role information...</Typography>
      </Box>
    );
  }

  const isAdmin = currentUserRole === 'admin';
  const canSeeSettings = permissions?.canManageSettings || permissions?.canManageUsers;

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Role Diagnostic
      </Typography>

      {message && (
        <Alert severity={message.includes('✅') ? 'success' : 'error'} sx={{ mb: 3 }}>
          {message}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Account Information
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography>
            <strong>User ID:</strong>{' '}
            <Box component="span" sx={{ bgcolor: 'grey.200', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {currentUser?.uid}
            </Box>
          </Typography>
          <Typography sx={{ mt: 1 }}><strong>Email:</strong> {currentUser?.email}</Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Company ID:</strong> {companyId || userDoc?.companyId || 'Not found'}
            {userDoc?.companyId && !companyId && ' (Found in user doc, but not loaded in context)'}
          </Typography>
          <Typography sx={{ mt: 1 }}><strong>Current Role:</strong> {currentUserRole || 'Not loaded'}</Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Can See Settings:</strong> {canSeeSettings ? '✅ Yes' : '❌ No'}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Is Admin:</strong> {isAdmin ? '✅ Yes' : '❌ No'}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Firestore Documents
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography><strong>Top-level User Doc:</strong></Typography>
          <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.875rem' }}>
            {userDoc ? JSON.stringify(userDoc, null, 2) : 'Not found'}
          </Box>

          {(companyId || userDoc?.companyId) && (
            <>
              <Typography sx={{ mt: 2 }}>
                <strong>Company User Doc:</strong> 
                {userDoc?.companyId && ` /companies/${userDoc.companyId}/users/${currentUser?.uid}`}
              </Typography>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.875rem' }}>
                {companyUserDoc ? JSON.stringify(companyUserDoc, null, 2) : '⚠️ NOT FOUND - This is likely the problem!'}
              </Box>
              {!companyUserDoc && userDoc?.companyId && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  The company user document is missing! Click "Fix My Role" below to create it.
                </Alert>
              )}
            </>
          )}
        </Box>
      </Paper>

      {((!isAdmin || !companyId || currentUserRole === null || !companyUserDoc) && userDoc?.companyId) && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.light' }}>
          <Typography variant="h6" gutterBottom color="warning.dark">
            Company Membership Issue Detected
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Your user document has a companyId: <strong>{userDoc.companyId}</strong>
          </Typography>
          {!companyUserDoc && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Your company user document is missing! This will be created automatically.
            </Alert>
          )}
          {companyUserDoc && (
            <Typography sx={{ mb: 2 }}>
              Your current role is: <strong>{currentUserRole || companyUserDoc.role || 'Not set'}</strong>
            </Typography>
          )}
          <Typography sx={{ mb: 2 }}>
            Click the button below to fix your role and create/update your company membership.
          </Typography>
          <Button
            variant="contained"
            color="warning"
            onClick={makeAdmin}
            disabled={fixing}
            sx={{ mr: 2 }}
          >
            {fixing ? <CircularProgress size={24} /> : 'Fix My Role (Make Admin)'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={async () => {
              // Force reload membership context
              if (window.location) {
                window.location.reload();
              }
            }}
            sx={{ mr: 2 }}
          >
            Refresh Page & Reload Context
          </Button>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={async () => {
              if (refreshMembership) {
                await refreshMembership();
                window.location.reload();
              } else {
                window.location.reload();
              }
            }}
          >
            Force Reload Membership
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
          {canSeeSettings && (
            <Button variant="outlined" onClick={() => navigate('/settings')}>
              Go to Settings
            </Button>
          )}
        </Box>
        
        {companyUserDoc?.role === 'admin' && !isAdmin && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Your Firestore document shows you're an admin, but the app context hasn't loaded it yet. 
              Click "Force Reload Membership" above, or try logging out and back in to refresh your session.
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}

